import * as React from "react";
import { useMutation, useQuery } from "react-query";
import { ApiError, AutoQuoteSettings, Branch, SystemSettings, SystemSettingsService } from "gen/clients";
import { Form, Formik } from "formik";
import { PageLoading } from "components/PageLoading/PageLoading";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { LoadingButton } from "../../../../components/LoadingButton/LoadingButton";
import { SnackbarApiError } from "../../../../components/SnackbarApiError/SnackbarApiError";
import { SnackbarAlert } from "../../../../components/SnackbarAlert/SnackbarAlert";
import Card from "@mui/material/Card";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import Typography from "@mui/material/Typography";
import { OpenAISettingsCard } from "./components/OpenAISettingsCard";
import { BranchSettingsTable } from "./components/BranchSettingsTable";
import { EditBranchSettingsDialog } from "./components/EditBranchSettingsDialog";
import { AddBranchSettingsDialog } from "./components/AddBranchSettingsDialog";
import { DeleteBranchSettingsDialog } from "./components/DeleteBranchSettingsDialog";
import { useDialogState } from "hooks/useDialogState";
import { useSnackbarAlert } from "hooks/useSnackbarAlert";

enum FieldNames {
  pricePerPage = "pricePerPage",
  pageCountToEscalateToHuman = "pageCountToEscalateToHuman",
  quoteExpirationPeriod = "quoteExpirationPeriod"
}

interface FormValues {
  [FieldNames.pricePerPage]: string | number;
  [FieldNames.pageCountToEscalateToHuman]: string | number;
  [FieldNames.quoteExpirationPeriod]: string | number;
}

const DEFAULT_PRICE_PER_PAGE = 125;
const PAGE_COUNT_ESCALATION_THRESHOLD = 30;
const QUOTE_EXPIRATION_DAYS = 30;

const AutoQuoteSettingsTab: React.FC = () => {
  const { snackbarAlert, openSnackbarAlert } = useSnackbarAlert({
    autoHideDuration: 5000,
    vertical: "bottom",
    horizontal: "left"
  });
  const [editDialogOpen, openEditDialog, closeEditDialog] = useDialogState();
  const [addDialogOpen, openAddDialog, closeAddDialog] = useDialogState();
  const [deleteDialogOpen, openDeleteDialog, closeDeleteDialog] = useDialogState();
  const [selectedBranch, setSelectedBranch] = React.useState<Branch | null>(null);
  const [selectedBranchCurrentSettings, setSelectedBranchCurrentSettings] = React.useState<AutoQuoteSettings | null>(
    null
  );
  const [autoQuoteSettingsByBranchId, setAutoQuoteSettingsByBranchId] = React.useState<
    Record<string, AutoQuoteSettings>
  >({});

  const {
    data: systemSettings,
    isLoading,
    error,
    refetch
  } = useQuery<SystemSettings, ApiError>(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings
  });

  const {
    mutateAsync: save,
    isLoading: isSaving,
    error: saveError,
    isSuccess: saveSuccess
  } = useMutation(SystemSettingsService.updateSystemSettings);

  // Initialize branch settings state when system settings load
  React.useEffect(() => {
    if (systemSettings?.autoQuoteSettingsByBranchId) {
      setAutoQuoteSettingsByBranchId(systemSettings.autoQuoteSettingsByBranchId);
    }
  }, [systemSettings]);

  const handleEditBranchSettings = React.useCallback(
    (branch: Branch, currentSettings: AutoQuoteSettings) => {
      setSelectedBranch(branch);
      setSelectedBranchCurrentSettings(currentSettings);
      openEditDialog();
    },
    [openEditDialog]
  );

  const handleAddBranchSettings = React.useCallback(() => {
    openAddDialog();
  }, [openAddDialog]);

  const handleSaveNewBranchSettings = React.useCallback(
    async (branchId: string, settings: AutoQuoteSettings) => {
      try {
        const updatedSettings = { ...autoQuoteSettingsByBranchId, [branchId]: settings };

        // Save to backend
        if (!systemSettings) {
          throw new Error("System settings not loaded");
        }

        await save({
          requestBody: {
            ...systemSettings,
            autoQuoteSettingsByBranchId: updatedSettings
          }
        });

        setAutoQuoteSettingsByBranchId(updatedSettings);
        refetch();
        openSnackbarAlert({ message: "Branch settings added successfully", severity: "success" });
      } catch (error) {
        openSnackbarAlert({ message: "Failed to add branch settings", severity: "error" });
        throw error; // Re-throw to let dialog handle it
      }
    },
    [autoQuoteSettingsByBranchId, systemSettings, save, refetch, openSnackbarAlert]
  );

  const handleDeleteBranchSettings = React.useCallback(
    (branch: Branch, currentSettings: AutoQuoteSettings) => {
      setSelectedBranch(branch);
      setSelectedBranchCurrentSettings(currentSettings);
      openDeleteDialog();
    },
    [openDeleteDialog]
  );

  const handleConfirmDeleteBranchSettings = React.useCallback(
    async (branchId: string) => {
      try {
        const updatedSettings = { ...autoQuoteSettingsByBranchId };
        delete updatedSettings[branchId];

        // Save to backend
        if (!systemSettings) {
          throw new Error("System settings not loaded");
        }

        await save({
          requestBody: {
            ...systemSettings,
            autoQuoteSettingsByBranchId: updatedSettings
          }
        });

        setAutoQuoteSettingsByBranchId(updatedSettings);
        refetch();
        openSnackbarAlert({ message: "Branch settings deleted successfully", severity: "success" });
      } catch (error) {
        openSnackbarAlert({ message: "Failed to delete branch settings", severity: "error" });
        throw error; // Re-throw to let dialog handle it
      }
    },
    [autoQuoteSettingsByBranchId, systemSettings, save, refetch, openSnackbarAlert]
  );

  const handleSaveBranchSettings = React.useCallback(
    async (branchId: string, settings: AutoQuoteSettings) => {
      try {
        const updatedSettings = { ...autoQuoteSettingsByBranchId, [branchId]: settings };

        // Save to backend
        if (!systemSettings) {
          throw new Error("System settings not loaded");
        }

        await save({
          requestBody: {
            ...systemSettings,
            autoQuoteSettingsByBranchId: updatedSettings
          }
        });

        setAutoQuoteSettingsByBranchId(updatedSettings);
        refetch();
        openSnackbarAlert({ message: "Branch settings updated successfully", severity: "success" });
      } catch (error) {
        openSnackbarAlert({ message: "Failed to update branch settings", severity: "error" });
        throw error; // Re-throw to let dialog handle it
      }
    },
    [autoQuoteSettingsByBranchId, systemSettings, save, refetch, openSnackbarAlert]
  );

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      if (!systemSettings) {
        return;
      }

      await save({
        requestBody: {
          ...systemSettings,
          autoQuoteSettings: {
            ...systemSettings.autoQuoteSettings,
            pricePerPage: +values.pricePerPage,
            pageCountEscalationThreshold: +values.pageCountToEscalateToHuman,
            quoteExpiration: +values.quoteExpirationPeriod
          }
        }
      });
      refetch();
    },
    [refetch, save, systemSettings]
  );

  if (isLoading) {
    return <PageLoading />;
  }

  if (error) {
    return <ApiErrorMessage apiError={error} />;
  }

  if (systemSettings) {
    return (
      <>
        <Formik
          initialValues={{
            [FieldNames.pricePerPage]: systemSettings?.autoQuoteSettings?.pricePerPage ?? DEFAULT_PRICE_PER_PAGE,
            [FieldNames.pageCountToEscalateToHuman]:
              systemSettings?.autoQuoteSettings?.pageCountEscalationThreshold ?? PAGE_COUNT_ESCALATION_THRESHOLD,
            [FieldNames.quoteExpirationPeriod]:
              systemSettings?.autoQuoteSettings?.quoteExpiration ?? QUOTE_EXPIRATION_DAYS
          }}
          onSubmit={onSubmit}
        >
          <Form>
            <Stack spacing={4} mt={2}>
              <Card sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Default Auto-Quote Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  These settings apply to all branches unless overridden by branch-specific settings below.
                </Typography>
                <FormControl fullWidth sx={{ p: 2 }}>
                  <FormGroup>
                    <Box mb={2}>
                      <TextInputField
                        name={FieldNames.pricePerPage}
                        label="Default Price Per Page"
                        helperText="Enter the default cost per page in USD (used when no branch-specific settings are configured)"
                        required
                      />
                    </Box>
                    <Box mb={2}>
                      <TextInputField
                        name={FieldNames.pageCountToEscalateToHuman}
                        label="Page Count To Escalate To Human"
                        helperText="Specify the number of pages at which a human review is required"
                        required
                      />
                    </Box>
                    <Box mb={2}>
                      <TextInputField
                        name={FieldNames.quoteExpirationPeriod}
                        label="Quote Expiration Period"
                        helperText="Quote expiration period in days"
                        required
                      />
                    </Box>
                  </FormGroup>
                </FormControl>
                <Box>
                  <LoadingButton isLoading={isSaving}>Save</LoadingButton>
                </Box>
              </Card>
            </Stack>
          </Form>
        </Formik>

        <BranchSettingsTable
          branches={systemSettings?.lltsBranches || []}
          autoQuoteSettingsByBranchId={autoQuoteSettingsByBranchId}
          defaultSettings={systemSettings?.autoQuoteSettings}
          onEditBranchSettings={handleEditBranchSettings}
          onAddBranchSettings={handleAddBranchSettings}
          onDeleteBranchSettings={handleDeleteBranchSettings}
        />

        {saveError && <SnackbarApiError error={saveError} />}
        {saveSuccess && <SnackbarAlert message="Operation completed successfully" severity="success" />}

        <OpenAISettingsCard systemSettings={systemSettings} systemSettingsRefetch={refetch} />

        {editDialogOpen && selectedBranch && selectedBranchCurrentSettings && (
          <EditBranchSettingsDialog
            open={editDialogOpen}
            branch={selectedBranch}
            currentSettings={selectedBranchCurrentSettings}
            onClose={closeEditDialog}
            onSave={handleSaveBranchSettings}
          />
        )}

        {addDialogOpen && (
          <AddBranchSettingsDialog
            open={addDialogOpen}
            availableBranches={(systemSettings?.lltsBranches || []).filter(
              branch => autoQuoteSettingsByBranchId[branch.id] === undefined
            )}
            defaultSettings={systemSettings?.autoQuoteSettings}
            onClose={closeAddDialog}
            onSave={handleSaveNewBranchSettings}
          />
        )}

        {deleteDialogOpen && selectedBranch && selectedBranchCurrentSettings && (
          <DeleteBranchSettingsDialog
            open={deleteDialogOpen}
            branch={selectedBranch}
            currentSettings={selectedBranchCurrentSettings}
            onClose={closeDeleteDialog}
            onConfirm={handleConfirmDeleteBranchSettings}
          />
        )}

        {snackbarAlert}
      </>
    );
  }

  return <></>;
};

export { AutoQuoteSettingsTab };
